import { useRef, useEffect, useState, useMemo } from 'react';
import { useMemoizedFn, usePrevious } from 'ahooks';
import { DetailTable } from 'xui-pro';
import useColumns from './useColumns';
import { CLICKFROM, STATUSTYPE } from '../utils';
import type { SelectValue } from '../utils';
import type { Part } from '@/api/epc/catalog/usageUsingPOST';
import useParentAutoHeight from '@/hooks/useParentAutoHeight';
import PartDetail from '../PartDetail';
import styles from './index.module.less';
import { Checkbox } from 'antd';
type PartListProps = {
  dataSource: Part[];
  selectValue: SelectValue;
  setSelectValue: (values: SelectValue) => void;
  showPartDetail: boolean;
  onClosePartDetail: () => void;
  onOpenPartDetail: () => void;
  loading: boolean;
  handlePartSearch: (partNo: string) => void;
  isInIframe?: boolean;
  vin?: string;
};

const PartList = (props: PartListProps) => {
  const {
    dataSource,
    selectValue,
    setSelectValue,
    showPartDetail,
    onOpenPartDetail,
    onClosePartDetail,
    isInIframe = false,
    vin
  } = props;
  const previousValue = usePrevious(selectValue);
  const partContainerRef = useRef<HTMLDivElement>(null);
  const partListRef = useRef<HTMLDivElement>(null);
  const [tableData, setTableData] = useState<Part[]>([]);
  const [firstCallOut, setFirstCallOut] = useState<Map<number, string>>(new Map());
  const [detailIndex, setDetailIndex] = useState<number>(-1);
  const [activeTabKey, setActiveTabKey] = useState<string>('1');
  const columns = useColumns(isInIframe);
  const { height } = useParentAutoHeight({
    parentContainerRef: partContainerRef,
    containerRef: partListRef,
    placeholderHeight: 76
  });
  const [checkValue, setCheckValue] = useState(false);

  const openDetail = useMemoizedFn(({ callout, partNo, activeKey }) => {
    const index = tableData.findIndex(item => item.callout === callout && item.partNo === partNo);
    if (index >= 0) {
      onOpenPartDetail();
      setDetailIndex(index);
      setActiveTabKey(!!activeKey ? activeKey : '1');
    }
    setDetailIndex(index);
  });

  const detailData = useMemo(() => {
    if (detailIndex >= 0) {
      return { ...tableData[detailIndex] };
    } else {
      return null;
    }
  }, [detailIndex, tableData]);

  useEffect(() => {
    setTableData([...dataSource]);
    setCheckValue(false);
  }, [dataSource]);

  useEffect(() => {
    const firstCallout = new Map();
    tableData.forEach(({ partNo, callout }) => {
      if (!firstCallout.has(callout)) {
        firstCallout.set(callout, partNo);
      } else return;
    });
    setFirstCallOut(firstCallout);
  }, [tableData]);

  /**
   * 行点击有三种情况触发
   * 1. 点击当前行, 默认高亮, 但是详情页已打开时，需切换至详情态
   * 2. 点击详情按钮，切换至详情态
   * 3. 点击3个图标，切换至详情态，并且：需要定位到对应tab，accessKey 1:照片 2:替换件 3:特别信息
   */
  const onRowClick = useMemoizedFn((e, record: any) => {
    let isClickDetail = false;
    const { callout, partNo } = record;
    const { accessKey, alt } = e.target;
    if (showPartDetail || e.target.innerHTML === '详情' || alt === 'partMoreInfo') {
      isClickDetail = true;
      const activeKey = alt === 'partMoreInfo' ? accessKey : '1';
      openDetail({ callout, partNo, activeKey });
    }
    setSelectValue({
      callout,
      partNo,
      source: CLICKFROM.PART,
      type: isClickDetail ? STATUSTYPE.DETAIL : STATUSTYPE.HIGHLIGHT
    });
  });

  /**
   * 将选中的行滚动到可视区域
   */
  useEffect(() => {
    const { source, isDefault, callout, partNo } = selectValue;
    if (source === CLICKFROM.LEGEND || isDefault) {
      const rows = partListRef.current?.querySelectorAll('.ant-table-row');
      if (rows) {
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i] as HTMLTableRowElement;
          const [rowCallout, rowPartNo] = row.getAttribute('data-row-key')?.split('_&&_') ?? [];
          const isSamePart = isDefault && partNo ? rowPartNo === partNo : true;
          // 默认进入选中当前，图例点击选中第一个
          if (rowCallout && rowCallout === String(callout) && isSamePart) {
            const container = partListRef.current?.querySelector('.ant-table-body');
            if (container) {
              const rowTop = row.offsetTop + (row.offsetParent as HTMLElement)?.offsetTop - 38;
              container.scrollTo({ top: rowTop, behavior: 'smooth' });
            }
            break;
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectValue]);

  const rowClassName = useMemoizedFn((record: any) => {
    const { callout, partNo } = record;
    const {
      callout: selectCallout,
      partNo: selectPartNo,
      source: selectSource,
      type: selectType
    } = selectValue;
    const { callout: previousCallout } = previousValue || {};
    // 1.在图例触发点击
    if (selectSource === CLICKFROM.LEGEND) {
      // 详情已打开时，在图例上切换callout, 命中的第一个展示为详情态
      const whenDetailOpenSwitch = showPartDetail && previousCallout !== selectCallout;
      const isFirstCallout = firstCallOut.get(selectCallout) === partNo;
      if (whenDetailOpenSwitch && selectCallout === callout) {
        if (isFirstCallout) {
          openDetail({ callout, partNo });
        }
        return isFirstCallout ? styles.detailRow : styles.highlightRow;
      }
      return selectCallout === callout ? styles.highlightRow : '';
    }

    // 2.在配件触发点击
    if (selectSource === CLICKFROM.PART && selectCallout === callout && selectPartNo === partNo) {
      if (selectType === STATUSTYPE.HIGHLIGHT) {
        return styles.highlightRow;
      }
      if (selectType === STATUSTYPE.DETAIL) {
        return styles.detailRow;
      }
    }
    return '';
  });

  /**
   * vin搜索的场景下，切换展示装车件LoadingPart
   * 注意：需要先关掉详情页，再切换数据源
   */
  const handleCheckboxChange = useMemoizedFn(e => {
    if (!vin) return;
    const { checked } = e.target;
    setCheckValue(checked);
    if (checked) {
      onClosePartDetail();
      setTableData(dataSource.filter(item => item.vinLoadingPart));
    } else {
      setTableData(dataSource);
    }
  });

  return (
    <div className={styles.partContainer} ref={partContainerRef}>
      <div className={styles.partList} ref={partListRef}>
        <DetailTable
          loading={props.loading}
          title='备件信息'
          dataSource={tableData}
          columns={columns}
          hasToolbar={false}
          pagination={false}
          rowKey={record => `${(record as any).callout}_&&_${(record as any).partNo}`}
          rowClassName={rowClassName}
          onRow={record => ({
            onClick: e => onRowClick(e, record)
          })}
          style={{ height: '100%' }}
          height={height}
          hasAutoHeight={false}
          emptyHeight={height - 30}
          hasEmptyPlaceholder={true}
          emptyPlaceholder='-'
          action={
            !!vin && (
              <Checkbox checked={checkValue} onChange={handleCheckboxChange}>
                仅显示该车适用配件
              </Checkbox>
            )
          }
        />
      </div>
      {showPartDetail && detailData && (
        <div className={styles.partDetail}>
          <PartDetail
            onClose={props.onClosePartDetail}
            initialValues={detailData || {}}
            handlePartSearch={props?.handlePartSearch}
            activeKey={activeTabKey}
            setActiveKey={setActiveTabKey}
            isInIframe={isInIframe}
          />
        </div>
      )}
    </div>
  );
};
export default PartList;
